import React, { useEffect, useRef, useState } from 'react';
import { Container, Form, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import styles from './bodywon.module.css';
import { fetchProductNameAutocomplete, getImageSrc } from '../utils/productStorefront';
import { useSettings } from '../context/SettingsContext';

const SearchModal = ({ show, onHide }) => {
  const navigate = useNavigate();
  const { formatPrice } = useSettings();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setQuery('');
      setResults([]);
      return undefined;
    }

    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;

    const term = query.trim();
    if (term.length < 1) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await fetchProductNameAutocomplete(term, 12);
        setResults(
          rows.map((row) => ({
            id: row.ProductId ?? row.productId,
            name: row.ProductName ?? row.productName ?? '',
            price: Number(row.Price ?? row.price ?? 0),
            imageSrc: getImageSrc(row.ImageUrl ?? row.imageUrl ?? ''),
          }))
        );
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, show]);

  useEffect(() => {
    if (!show) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onHide();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [show, onHide]);

  const handleSelectProduct = (productId) => {
    onHide();
    navigate(`/product/${productId}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (results[0]?.id) handleSelectProduct(results[0].id);
  };

  if (!show) return null;

  const hasQuery = query.trim().length > 0;

  return (
    <>
      <button type="button" className={styles.searchBackdrop} aria-label="Close search" onClick={onHide} />
      <div className={styles.searchTopPanel}>
        <Container>
          <div className={styles.searchTopBar}>
            <Form onSubmit={handleSubmit} className={styles.searchTopForm}>
              <InputGroup className={styles.searchInputGroup}>
                <Form.Control
                  ref={inputRef}
                  placeholder="Search entire store here..."
                  className={styles.searchInput}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <InputGroup.Text className={styles.searchInputIcon}>
                  <FiSearch />
                </InputGroup.Text>
              </InputGroup>
            </Form>
            <button type="button" className={styles.searchCloseBtn} onClick={onHide} aria-label="Close">
              <FiX size={22} />
            </button>
          </div>

          {hasQuery && (
            <div className={styles.searchResultsPanel}>
              {loading ? (
                <p className={styles.searchAutocompleteHint}>Searching...</p>
              ) : results.length === 0 ? (
                <p className={styles.searchAutocompleteHint}>No products found.</p>
              ) : (
                <div className={styles.searchResultsGrid}>
                  {results.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      className={styles.searchResultCard}
                      onClick={() => handleSelectProduct(product.id)}
                    >
                      <div className={styles.searchResultImage}>
                        {product.imageSrc ? (
                          <img src={product.imageSrc} alt={product.name} />
                        ) : (
                          <div className={styles.searchResultPlaceholder} />
                        )}
                      </div>
                      <div className={styles.searchResultInfo}>
                        <p className={styles.searchResultName}>{product.name}</p>
                        <p className={styles.searchResultPrice}>{formatPrice(product.price)}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default SearchModal;
